import { z } from "zod";

import { InvoiceDueDateSchema } from "~/lib/schemas/invoice";
import { ArrayOfDailyDurationSchema } from "~/lib/schemas/session";

import { EngagementStatus } from "../../../drizzle/schema";

import {
  SCHEMA_LIMITS,
  createMaxLengthMessage,
  ALLOWED_DURATIONS,
  isAllowedDuration,
} from "./constants";

export const CoachEngagementCardsQuerySchema = z
  .object({
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(10),
    statuses: z.array(z.nativeEnum(EngagementStatus)).min(1).optional(),
    sort: z
      .object({
        sortBy: z.enum([
          "next_session",
          "status",
          "coachFirstName",
          "end_date",
        ]),
        sortOrder: z.enum(["asc", "desc"]).default("asc"),
      })
      .optional(),
  })
  .default({ page: 1, limit: 10 });

export const CoachesClientsSchema = z.object({
  search: z.string().optional(),
});

export const UserEngagementsSchema = z
  .object({
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(10),
    status: z.nativeEnum(EngagementStatus).optional(),
  })
  .default({ page: 1, limit: 10 });

export const EngagementCoachIdSchema = z.object({
  coachId: z.string().uuid(),
});

/** A name the coach types for a new client. */
const ClientNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(SCHEMA_LIMITS.CLIENT_NAME_MAX_LENGTH);

export const CreateEngagementSchema = z.object({
  client_id: z.string().uuid().optional(),
  client_email: z.string().email().optional(),
  /** Stored only on an account this engagement creates; never on an existing one. */
  client_first_name: ClientNameSchema.optional(),
  client_last_name: ClientNameSchema.optional(),
  start_date: z.string().datetime(),
  end_date: z.string().datetime(),
  /**
   * Optional default meeting link applied to sessions that don't carry their own
   * `meeting_url`. Coaches may schedule links per session later, so this can be omitted.
   */
  meeting_link: z.string().url().optional(),
  sessions: ArrayOfDailyDurationSchema.nonempty(),
  /** Ask the client to share initial goals during onboarding (per-invite choice). */
  onboarding_goals_requested: z.boolean().optional().default(false),
  /** The coach's template the program starts from; its homework is scheduled. */
  program_template_id: z.string().uuid().optional(),
  // Optional: an empty schedule means this is a free program (no invoices).
  invoice_schedule: z
    .array(
      z.object({
        invoice_date: z.string().datetime(),
        invoice_amount: z.number().int().positive(),
        description: z.string().optional(),
        currency: z.string().default("USD"),
      }),
    )
    .default([]),
});

export const CoachClientRelationshipSchema = z.object({
  search: z.string().optional(),
});

export const EditEngagementSchema = z.object({
  engagement_id: z.string().uuid(),
  /** Optional default meeting link; when provided it is applied to the updated sessions. */
  meeting_link: z.string().url().optional(),
  session_time_updates: z
    .array(
      z.object({
        session_id: z.string().uuid(),
        start: z.string().datetime(),
        duration: z
          .number()
          .int()
          .refine(isAllowedDuration, {
            message: `duration must be one of ${ALLOWED_DURATIONS.join(", ")}`,
          }),
      }),
    )
    .optional()
    .default([]),
  invoice_schedule_updates: z
    .array(
      z.object({
        stripe_invoice_id: z.string(),
        invoice_date: z.string().datetime().optional(),
        invoice_amount: z.number().int().positive().optional(),
        description: z.string().optional(),
      }),
    )
    .optional()
    .default([]),
});

export const CreateResourceSchema = z.object({
  content: z.string(),
  engagementId: z.string().uuid(),
  uploadModel: z.enum(["file", "url", "agreement"]).default("file"),
  title: z
    .string()
    .max(
      SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH,
      createMaxLengthMessage("Title", SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH),
    ),
  description: z.string(),
  imageSrc: z.string().optional(),
  publishedAt: z.string().datetime(),
  externalLinkUrl: z.string().optional(),
  externalLinkText: z.string().optional(),
});

export const ArrayTagSchema = z
  .array(
    z.object({
      id: z.string().uuid(),
    }),
  )
  .nonempty();

export const GetResourceUploadUrlSchema = z.object({
  engagementId: z.string().uuid(),
  filename: z.string(),
  contentType: z.string(),
  externalLinkUrl: z.string().optional(),
  sessionId: z.string().uuid().optional(),
  uploadModel: z.enum(["file", "agreement"]).default("file"),
  title: z
    .string()
    .max(
      SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH,
      createMaxLengthMessage("Title", SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH),
    ),
  tagIds: z.array(z.string().uuid()).optional(),
  publishedAt: z.string().datetime().optional(),
  expirationDate: z.date().optional(),
});

export const CreateEngResourceAfterUploadSchema = z.object({
  engagementId: z.string().uuid(),
  name: z.string(),
  externalLinkUrl: z.string().optional(),
  sessionId: z.string().uuid().optional(),
  title: z
    .string()
    .max(
      SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH,
      createMaxLengthMessage("Title", SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH),
    ),
  contentType: z.string().optional(),
  tagIds: z.array(z.string().uuid()).default([]),
  publishedAt: z.string().datetime().optional(),
  filename: z.string().optional(),
});

export const GetUpdateEngResourceUploadUrlSchema = z.object({
  resourceId: z.string().uuid(),
  filename: z.string(),
  contentType: z.string(),
});

export const FinalizeUpdateEngResourceSchema = z.object({
  resourceId: z.string().uuid(),
  uploadModel: z.enum(["file", "url", "agreement"]).default("file"),
  title: z
    .string()
    .max(
      SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH,
      createMaxLengthMessage("Title", SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH),
    )
    .optional(),
  externalLinkUrl: z.string().url().optional(),
  newName: z.string().optional(),
  expirationDate: z
    .date()
    .refine((date) => date > new Date(), {
      message: "Expiration date must be in the future",
    })
    .optional(),
  tagIds: z.array(z.string().uuid()).default([]),
});

export const GetEngResourceDownloadInfoSchema = z.object({
  resourceId: z.string().uuid(),
});

export const DeleteEngResourceSchema = z.object({
  resourceId: z.string().uuid(),
});

export const GetAllEngagementResourcesSchema = z.object({
  engagementId: z.string().uuid(),
});

export const GetAllEngagementResourcesByUploadModelSchema = z.object({
  engagementId: z.string().uuid(),
  uploadModel: z.enum(["file", "agreement"]).default("file"),
});

export const EngagementResourcesByCoachSchema = z.object({
  engagementId: z.string().uuid(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(10),
  tagIds: z.array(z.string().uuid()).optional(),
});

export const EngagementResourceTagsSchema = z.object({
  engagementId: z.string().uuid(),
});

export const UserInfoForEngagementSchema = z.object({
  engagementId: z.string().uuid(),
});

export const UpdateEngagementStatusSchema = z.object({
  engagementId: z.string().uuid(),
  status: z.enum(["active", "paused", "cancelled", "archived", "finished"]),
  coachTimeZone: z.string(),
});

export const PaymentInfoForEngagementSchema = z.object({
  engagementId: z.string().uuid(),
});

export const PaymentHistoryForEngagementSchema = z.object({
  engagementId: z.string().uuid(),
  filter: z.enum(["paid", "pending", "future", "cancelled"]).optional(),
  limit: z.number().int().min(1).max(100).default(10),
  page: z.number().int().min(1).default(1),
});

export const EditEngagementInvoiceSchema = z.object({
  id: z.string().uuid(),
  engagementId: z.string().uuid(),
  invoiceDate: InvoiceDueDateSchema.optional(),
  invoiceAmount: z.number().int().positive().optional(),
  description: z.string().optional(),
  currency: z.string().optional(),
});

export const DeleteEngagementInvoiceSchema = z.object({
  engagementId: z.string().uuid(),
  id: z.string().uuid(),
});

export const GetEngagementSessionProgressSchema = z.object({
  engagementId: z.string().uuid(),
});

export const FinalizeCreateEngResourceSchema = z.object({
  resourceId: z.string().uuid(),
  engagementId: z.string().uuid(),
  sessionId: z.string().uuid().optional(),
  filename: z.string().min(1, "File name is required"),
  title: z
    .string()
    .min(1, "Title is required")
    .max(
      SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH,
      createMaxLengthMessage("Title", SCHEMA_LIMITS.RESOURCE_TITLE_MAX_LENGTH),
    ),
  uploadModel: z.enum(["file", "agreement"]).default("file"),
  expirationDate: z
    .date()
    .refine((date) => date > new Date(), {
      message: "Expiration date must be in the future",
    })
    .optional(),
  tagIds: z.array(z.string().uuid()).default([]),
});

export type FinalizeCreateEngResourceData = z.infer<
  typeof FinalizeCreateEngResourceSchema
>;
export type EditEngagementInput = z.infer<typeof EditEngagementSchema>;
export type ArrayTagInput = z.infer<typeof ArrayTagSchema>;
export type CreateResourceInput = z.infer<typeof CreateResourceSchema>;
export type CreateEngagementInput = z.infer<typeof CreateEngagementSchema>;
export type CoachEngagementCardsQuery = z.infer<
  typeof CoachEngagementCardsQuerySchema
>;
export type GetResourceUploadUrlData = z.infer<
  typeof GetResourceUploadUrlSchema
>;
export type FinalizeUpdateEngResourceData = z.infer<
  typeof FinalizeUpdateEngResourceSchema
>;
